<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import GlassNavbar from '@/components/ui/GlassNavbar.vue'
import RoleSidebar from '@/components/RoleSidebar.vue'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import { useIdleTimer } from '@/composables/useIdleTimer'
import IdleTimeoutModal from '@/components/ui/IdleTimeoutModal.vue'
import TopProgressBar from '@/components/ui/TopProgressBar.vue'
import NotificationToast from '@/components/ui/NotificationToast.vue'
import AppConfirmModal from '@/components/ui/AppConfirmModal.vue'
import ShortcutsHelpModal from '@/components/ui/ShortcutsHelpModal.vue'
import AICopilotWidget from '@/components/ai/AICopilotWidget.vue'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'

const authStore = useAuthStore()
const loading = useLoadingStore()
const router = useRouter()
const { start, stop, resetTimer, isWarningActive, remainingSeconds } = useIdleTimer(15, 1) // 15m total, 1m warning

const isSidebarCollapsed = ref(false)
const showShortcutsHelp = ref(false)

const handleSidebarToggle = (event: Event) => {
  const customEvent = event as CustomEvent
  isSidebarCollapsed.value = customEvent.detail.collapsed
}

const toggleShortcutsHelp = () => {
  if (authStore.isAuthenticated) {
    showShortcutsHelp.value = !showShortcutsHelp.value
  }
}

const toggleSidebar = () => {
  if (authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('toggle-sidebar'))
  }
}

const navigateTo = (path: string) => {
  if (authStore.isAuthenticated) {
    router.push(path)
  }
}

const navigateDashboard = () => {
  if (!authStore.isAuthenticated) return
  const role = authStore.user?.role
  if (role === 'ADMIN') {
    router.push('/admin')
  } else if (role === 'SUPERVISOR') {
    router.push('/supervisor')
  } else if (role === 'CUSTOMER') {
    router.push('/customer')
  } else {
    router.push('/')
  }
}

const handleLogout = async () => {
  if (authStore.isAuthenticated) {
    await authStore.logout()
    router.push('/login')
  }
}

useKeyboardShortcuts([
  { key: 'h', alt: true, handler: toggleShortcutsHelp },
  { key: '?', handler: toggleShortcutsHelp },
  { key: 'm', alt: true, handler: toggleSidebar },
  { key: 'd', alt: true, handler: navigateDashboard },
  { key: 'a', alt: true, handler: () => navigateTo('/supervisor/animals') },
  { key: 's', alt: true, handler: () => navigateTo('/shops/products') },
  { key: 'b', alt: true, handler: () => navigateTo('/shops/butcher/sales') },
  { key: 'g', alt: true, handler: () => navigateTo('/shops/grocery/sales') },
  { key: 'k', alt: true, handler: () => navigateTo('/shops/local-harvest/sales') },
  { key: 'w', alt: true, handler: () => navigateTo('/workers') },
  { key: 'e', alt: true, handler: () => navigateTo('/equipment') },
  { key: 'x', alt: true, handler: handleLogout },
  {
    key: 'Escape',
    handler: () => {
      if (showShortcutsHelp.value) showShortcutsHelp.value = false
    },
    allowInInputs: true
  }
])

onMounted(async () => {
  if (authStore.isAuthenticated) {
    start()
  }
  
  window.addEventListener('sidebar-toggle', handleSidebarToggle)
})

onUnmounted(() => {
  window.removeEventListener('sidebar-toggle', handleSidebarToggle)
})

watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      start()
    } else {
      stop()
    }
  },
)
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Navbar is now optional or can be integrated differently, keeping for now but it might overlap with new sidebar header on mobile -->
    <!-- Ideally, the GlassNavbar should be adjusted or hidden on mobile if the sidebar handles everything -->
    <!-- For this hybrid approach, we usually keep a top bar for context or actions -->
    <GlassNavbar class="md:hidden print:hidden" :is-sidebar-collapsed="isSidebarCollapsed" /> 

    <RoleSidebar v-if="authStore.isAuthenticated" />
    
    <main
      :class="[
        authStore.isAuthenticated ? (isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64') : 'ml-0',
        'transition-all duration-300 ease-in-out flex-1 p-6 print:m-0 print:p-0 print:w-full print:max-w-none print:ml-0'
      ]"
    >
      <!-- Spacer for mobile navbar -->
      <div class="h-16 md:h-0 print:hidden"></div> 
      <RouterView :key="$route.fullPath" />
    </main>

    <!-- FULL SCREEN LOADING OVERLAY -->
    <div
      v-if="loading.isLoading"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm print:hidden"
    >
      <div class="flex flex-col items-center">
         <div class="h-12 w-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4"></div>
         <span class="text-white font-medium tracking-wide">Processing...</span>
      </div>
    </div>

    <!-- IDLE TIMEOUT MODAL -->
    <IdleTimeoutModal
      :show="isWarningActive"
      :remaining-seconds="remainingSeconds"
      @stay="resetTimer"
      @logout="authStore.logout()"
      class="print:hidden"
    />

    <!-- GLOBAL PROGRESS BAR -->
    <TopProgressBar class="print:hidden" />

    <!-- GLOBAL NOTIFICATIONS -->
    <NotificationToast class="print:hidden" />

    <!-- GLOBAL CONFIRM MODAL -->
    <AppConfirmModal class="print:hidden" />

    <!-- SHORTCUTS HELP MODAL -->
    <ShortcutsHelpModal :show="showShortcutsHelp" @close="showShortcutsHelp = false" class="print:hidden" />

    <!-- AI COPILOT BILINGUAL ASSISTANT -->
    <AICopilotWidget v-if="authStore.isAuthenticated" class="print:hidden" />
  </div>
</template>
